import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Truck, Wrench, Package, ArrowUpRight, Instagram } from 'lucide-react';
import { companyInfo, navLinks } from '../../data/mock';

export const Footer = () => {
  return (
    <footer className="relative bg-black text-white overflow-hidden">
      {/* Plant wall background met NEXON-logo */}
      <div className="absolute inset-0">
        <img
          src="/nexon-wall.webp"
          alt="NEXON Installatie & Service plantenwand"
          className="w-full h-full object-cover opacity-70"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-yellow-400/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 py-12 lg:py-16 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Company info */}
          <div className="lg:col-span-1">
            <p className="text-neutral-400 mb-8 leading-relaxed">
              {companyInfo.description}
            </p>
            <div className="flex gap-3">
              {[Truck, Wrench, Package].map((Icon, idx) => (
                <div key={idx} className="w-10 h-10 bg-white/5 hover:bg-yellow-400/20 rounded-xl flex items-center justify-center transition-colors cursor-pointer group">
                  <Icon className="w-5 h-5 text-yellow-400 group-hover:scale-110 transition-transform" />
                </div>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
              <div className="w-2 h-2 bg-yellow-400 rounded-full" />
              Navigatie
            </h3>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="group flex items-center text-neutral-400 hover:text-yellow-400 transition-colors"
                  >
                    <ArrowUpRight className="w-4 h-4 mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
              <div className="w-2 h-2 bg-yellow-400 rounded-full" />
              Diensten
            </h3>
            <ul className="space-y-4 text-neutral-400">
              {[
                { icon: Truck, text: 'Transport fitnessapparatuur' },
                { icon: Wrench, text: 'Installatie & montage' },
                { icon: Package, text: 'Onderhoud & verplaatsing' }
              ].map((item) => (
                <li key={item.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-yellow-400" />
                  </div>
                  <span className="text-sm">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
              <div className="w-2 h-2 bg-yellow-400 rounded-full" />
              Contact
            </h3>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="group flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-all"
                >
                  <div className="w-12 h-12 bg-yellow-400 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">Telefoon</div>
                    <div className="font-semibold text-white">{companyInfo.phone}</div>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="group flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-all"
                >
                  <div className="w-12 h-12 bg-neutral-800 border border-neutral-700 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Mail className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">E-mail</div>
                    <div className="font-semibold text-white">{companyInfo.email}</div>
                  </div>
                </a>
              </li>
              <li className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                <div className="w-12 h-12 bg-neutral-800 border border-neutral-700 rounded-xl flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <div className="text-xs text-neutral-500">Locatie</div>
                  <div className="font-semibold text-white">{companyInfo.location}</div>
                </div>
              </li>
              <li>
                <a
                  href={companyInfo.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Volg ons op Instagram"
                  data-testid="footer-instagram-link"
                  className="group flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-all"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Instagram className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">Instagram</div>
                    <div className="font-semibold text-white">Volg ons</div>
                  </div>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-neutral-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-neutral-500">
              © {new Date().getFullYear()} Nexon Installatie & Service, Rotterdam. Alle rechten voorbehouden.
            </p>
            <p className="text-sm text-neutral-500 flex flex-wrap justify-center gap-2">
              <span className="text-yellow-400">•</span> Installatie 
              <span className="text-yellow-400">•</span> Transport 
              <span className="text-yellow-400">•</span> Fitnessapparatuur 
              <span className="text-yellow-400">•</span> Onderhoud
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
